import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import {
  previewFacebookZipTargeting,
  fetchFacebookAdSetTargeting,
} from '@src/api/actions';

function buildDiff(liveZips, newZips) {
  const liveSet = new Set(liveZips);
  const newSet = new Set(newZips);
  let adds = 0;
  let removes = 0;
  newSet.forEach((zip) => {
    if (!liveSet.has(zip)) adds += 1;
  });
  liveSet.forEach((zip) => {
    if (!newSet.has(zip)) removes += 1;
  });
  return { liveCount: liveSet.size, newCount: newSet.size, adds, removes };
}

/**
 * Pre-push review. `groups` is a list of push batches — one per campaign
 * vertical when vertical matching is on, otherwise a single batch carrying
 * the Step 2 vertical selection. Each group gets its own ZIP set and
 * per-ad-set live→new diff.
 */
function PushConfirmDialog({
  open,
  onClose,
  onConfirm,
  pushing,
  pushError,
  clientName,
  clientId,
  adAccount,
  campaigns,
  groups,
  matchVerticals,
  verticalNames,
  territories,
  territoryIds,
  includeFilteredOut,
}) {
  const [diffLoading, setDiffLoading] = useState(false);
  const [groupDiffs, setGroupDiffs] = useState([]);
  const [diffError, setDiffError] = useState('');

  useEffect(() => {
    if (!open) {
      setGroupDiffs([]);
      setDiffError('');
      return undefined;
    }

    let cancelled = false;
    setDiffLoading(true);
    setDiffError('');

    const load = async () => {
      try {
        const loaded = await Promise.all(
          groups.map(async (group) => {
            const previewResponse = await previewFacebookZipTargeting({
              clientId,
              verticalIds: group.verticalIds,
              territoryIds,
              includeFilteredOut,
              sampleSize: 1,
              includeZipCodes: true,
            });
            const newZips = previewResponse?.data?.zip_codes || [];
            const rows = await Promise.all(
              group.adSets.map(async (adSet) => {
                try {
                  const response = await fetchFacebookAdSetTargeting({
                    clientId,
                    adAccountId: adAccount?.account_id,
                    adSetId: adSet.ad_set_id,
                  });
                  const liveZips = [
                    ...(response?.data?.direct_zip_codes || []),
                    ...(response?.data?.db_cluster_zip_codes || []),
                  ];
                  return {
                    adSetId: adSet.ad_set_id,
                    adSetName: adSet.ad_set_name || adSet.ad_set_id,
                    ...buildDiff(liveZips, newZips),
                  };
                } catch (error) {
                  return {
                    adSetId: adSet.ad_set_id,
                    adSetName: adSet.ad_set_name || adSet.ad_set_id,
                    error:
                      error?.message || 'Could not load current targeting.',
                    newCount: newZips.length,
                  };
                }
              })
            );
            return {
              key: group.key,
              label: group.label,
              unmatched: group.unmatched,
              zipCount: newZips.length,
              rows,
            };
          })
        );
        if (!cancelled) {
          setGroupDiffs(loaded);
        }
      } catch (error) {
        if (!cancelled) {
          setDiffError(
            error?.message ||
              'Could not compute the targeting diff. You can still push, but review the selection carefully.'
          );
        }
      } finally {
        if (!cancelled) {
          setDiffLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sharedAccount = (adAccount?.client_count || 0) > 1;
  const selectedVerticalNamesLower = verticalNames.map((name) =>
    (name || '').toLowerCase()
  );
  // With vertical matching on, campaign↔vertical mismatch is impossible by
  // construction — each campaign receives its own vertical's ZIPs.
  const mismatchedCampaigns = matchVerticals
    ? []
    : campaigns.filter((campaign) => {
        if (!campaign.vertical) {
          return false;
        }
        return selectedVerticalNamesLower.some(
          (name) => name && name !== campaign.vertical.toLowerCase()
        );
      });
  const unmatchedGroup = groups.find((group) => group.unmatched);
  const exhaustedTerritories = territories.filter(
    (territory) => territory.budgeted && territory.exhausted
  );
  const unbudgetedTerritories = territories.filter(
    (territory) => !territory.budgeted && !territory.clientWideFunded
  );
  const emptyGroups = groupDiffs.filter((group) => group.zipCount === 0);
  const totalNewZips = groupDiffs.reduce(
    (sum, group) => sum + group.zipCount,
    0
  );

  return (
    <Dialog open={open} onClose={pushing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm ZIP push</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                Client
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {clientName || `Client ${clientId}`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                Ad account
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {adAccount?.account_name || adAccount?.account_id}
              </Typography>
              {sharedAccount && (
                <Chip
                  label={`Shared · ${adAccount.client_count} clients`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                Campaigns
              </Typography>
              <Stack spacing={0.25}>
                {campaigns.map((campaign) => (
                  <Stack key={campaign.id} direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{campaign.name}</Typography>
                    {campaign.vertical && (
                      <Chip label={campaign.vertical} size="small" variant="outlined" />
                    )}
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                Mode
              </Typography>
              <Box>
                {matchVerticals ? (
                  <Typography variant="body2">
                    Vertical-matched — each campaign&apos;s ad sets receive that
                    campaign&apos;s own vertical ZIPs.
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    Single batch — all ad sets receive the same ZIP set (
                    {verticalNames.join(', ') || 'no verticals'}).
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Territories: {territories.map((territory) => territory.name).join(', ') || '—'}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">
              Targeting change per ad set
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pushing replaces each ad set&apos;s ZIP targeting entirely — it does not add to it.
            </Typography>
            {diffLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading current Facebook targeting…
                </Typography>
              </Stack>
            ) : diffError ? (
              <Alert severity="warning">{diffError}</Alert>
            ) : (
              groupDiffs.map((group) => (
                <Stack key={group.key} spacing={0.75}>
                  {(groupDiffs.length > 1 || group.label) && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={group.label || 'Selected verticals'}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {group.zipCount.toLocaleString()} ZIPs
                      </Typography>
                    </Stack>
                  )}
                  {group.rows.map((row) => (
                    <Stack
                      key={row.adSetId}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ pl: groupDiffs.length > 1 || group.label ? 1.5 : 0 }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {row.adSetName}
                      </Typography>
                      {row.error ? (
                        <Typography variant="body2" color="warning.main">
                          current targeting unavailable → {row.newCount} ZIPs
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {row.liveCount} live → {row.newCount} new
                          </Typography>
                          <Chip
                            label={`+${row.adds}`}
                            size="small"
                            color={row.adds ? 'success' : 'default'}
                            variant="outlined"
                          />
                          <Chip
                            label={`−${row.removes}`}
                            size="small"
                            color={row.removes ? 'error' : 'default'}
                            variant="outlined"
                          />
                        </>
                      )}
                    </Stack>
                  ))}
                </Stack>
              ))
            )}
          </Stack>

          {(mismatchedCampaigns.length > 0 ||
            exhaustedTerritories.length > 0 ||
            emptyGroups.length > 0 ||
            Boolean(unmatchedGroup) ||
            sharedAccount) && <Divider />}

          {mismatchedCampaigns.map((campaign) => (
            <Alert key={`mismatch-${campaign.id}`} severity="warning">
              Vertical mismatch: selection includes{' '}
              {verticalNames
                .filter(
                  (name) =>
                    (name || '').toLowerCase() !== campaign.vertical.toLowerCase()
                )
                .join(', ')}{' '}
              ZIPs but campaign “{campaign.name}” is tagged {campaign.vertical}. Continue
              only if intentional.
            </Alert>
          ))}
          {matchVerticals && unmatchedGroup && (
            <Alert severity="warning">
              {unmatchedGroup.adSets.length} ad set
              {unmatchedGroup.adSets.length === 1 ? '' : 's'} belong to campaigns
              without a vertical tag — they receive the Step 2 vertical selection (
              {verticalNames.join(', ') || 'none'}).
            </Alert>
          )}
          {emptyGroups.map((group) => (
            <Alert key={`empty-${group.key}`} severity="warning">
              No ZIPs match for {group.label || 'the selected verticals'} with the
              current territory selection — its ad sets would fail to push.
            </Alert>
          ))}
          {exhaustedTerritories.length > 0 && (
            <Alert severity="warning">
              No budget left this month for:{' '}
              {exhaustedTerritories.map((territory) => territory.name).join(', ')}.
            </Alert>
          )}
          {sharedAccount && (
            <Alert severity="warning">
              This ad account serves {adAccount.client_count} clients — double-check the
              campaign belongs to {clientName || 'the selected client'}.
            </Alert>
          )}
          {unbudgetedTerritories.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              No budget found this month for{' '}
              {unbudgetedTerritories.map((territory) => territory.name).join(', ')}.
            </Typography>
          )}

          {pushError && <Alert severity="error">{pushError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pushing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          disabled={pushing || diffLoading || (!diffError && totalNewZips === 0)}
          endIcon={pushing ? <CircularProgress size={18} /> : null}
        >
          Push to Facebook
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PushConfirmDialog;
